import PanelMarkdown from '@/components/PanelMarkdown';
import ReportHtmlFrame from '@/components/ReportHtmlFrame';
import IntegrationSuiteIflowHeader from '@/components/IntegrationSuiteIflowHeader';
import IntegrationSuiteMermaid from '@/components/IntegrationSuiteMermaid';
import {
  coerceIntegrationSuitePanelBlocks,
  extractIflowArtifactMeta,
  sortIntegrationSuitePanelBlocks,
} from '@/lib/integrationSuiteArtifact';
import { cn } from '@/lib/utils';
import { useMemo, useState } from 'react';

function blockLabel(block, index) {
  const type = String(block?.type || 'markdown').toLowerCase();
  const title = String(block?.title || '').trim();
  if (title) return title;
  if (type === 'mermaid') return 'Flow diagram';
  if (type === 'json') return 'Metadata';
  return `${type.charAt(0).toUpperCase()}${type.slice(1)} ${index + 1}`;
}

function JsonBlock({ content, parsed }) {
  const text = useMemo(() => {
    if (parsed && typeof parsed === 'object') {
      try {
        return JSON.stringify(parsed, null, 2);
      } catch {
        /* fall through */
      }
    }
    return String(content || '').trim();
  }, [content, parsed]);

  return (
    <pre className="overflow-auto whitespace-pre-wrap break-words rounded-lg border border-cp-line bg-cp-console p-4 font-mono text-xs leading-relaxed text-[#e8edf7]">
      {text || '—'}
    </pre>
  );
}

function MarkdownBlock({ content }) {
  return (
    <div className="rounded-lg border border-cp-line bg-cp-inset p-4">
      <PanelMarkdown content={content} />
    </div>
  );
}

function HtmlBlock({ content, fillHeight }) {
  const html = String(content || '').trim();
  if (!html) return <MarkdownBlock content="" />;
  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border border-cp-line bg-cp-surface',
        fillHeight ? 'min-h-[12rem] flex-1' : 'max-h-[min(420px,55vh)]'
      )}
    >
      <ReportHtmlFrame
        html={html}
        title="Integration Suite preview"
        className={cn('w-full border-0', fillHeight ? 'min-h-[12rem] h-full' : 'h-[min(420px,55vh)]')}
      />
    </div>
  );
}

export default function IntegrationSuitePanelBlocks({ blocks = [], summary = '', fillHeight = false }) {
  const normalized = useMemo(() => {
    const raw = coerceIntegrationSuitePanelBlocks(blocks, summary);
    return sortIntegrationSuitePanelBlocks(raw);
  }, [blocks, summary]);

  const iflowMeta = useMemo(() => extractIflowArtifactMeta(normalized), [normalized]);
  const mermaidBlocks = useMemo(
    () => normalized.filter((b) => String(b.type || '').toLowerCase() === 'mermaid'),
    [normalized]
  );
  const detailBlocks = useMemo(
    () => normalized.filter((b) => String(b.type || '').toLowerCase() !== 'mermaid'),
    [normalized]
  );
  const [tab, setTab] = useState(0);

  if (!normalized.length) {
    return summary ? <MarkdownBlock content={summary} /> : null;
  }

  const active = detailBlocks[Math.min(tab, detailBlocks.length - 1)] || detailBlocks[0];
  const type = active ? String(active.type || 'markdown').toLowerCase() : 'markdown';

  return (
    <div className={cn('space-y-3', fillHeight && 'flex min-h-0 flex-1 flex-col')}>
      <IntegrationSuiteIflowHeader meta={iflowMeta} />

      {mermaidBlocks.map((block, index) => (
        <IntegrationSuiteMermaid
          key={`is-mermaid-${index}`}
          content={block.content}
          className={fillHeight ? 'min-h-[18rem]' : 'min-h-[16rem] max-h-[min(560px,70vh)]'}
        />
      ))}

      {detailBlocks.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {detailBlocks.map((block, index) => (
            <button
              key={`is-panel-${index}`}
              type="button"
              onClick={() => setTab(index)}
              className={cn(
                'rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                tab === index
                  ? 'border-cp-indigo bg-cp-inset text-cp-indigo'
                  : 'border-cp-line bg-cp-surface text-muted-foreground hover:text-cp-fg'
              )}
            >
              {blockLabel(block, index)}
            </button>
          ))}
        </div>
      )}
      {detailBlocks.length > 0 && (
        <div className={cn(fillHeight && 'min-h-0 flex-1 overflow-auto')}>
          {type === 'html' && <HtmlBlock content={active.content} fillHeight={fillHeight} />}
          {type === 'json' && <JsonBlock content={active.content} parsed={active.parsed} />}
          {(type === 'markdown' || !['html', 'json'].includes(type)) && (
            <MarkdownBlock content={active.content} />
          )}
        </div>
      )}
    </div>
  );
}

export function integrationSuiteCopyPayload(blocks, summary) {
  const normalized = Array.isArray(blocks) ? sortIntegrationSuitePanelBlocks(blocks) : [];
  if (normalized.length) {
    return normalized
      .map((b, i) => {
        const label = blockLabel(b, i);
        return `## ${label}\n${String(b.content || '').trim()}`;
      })
      .join('\n\n');
  }
  return String(summary || '').trim();
}
